import React, { useEffect, useState } from "react";

//UI Material
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

//Material Icons
import {
  Add,
  AssignmentInd,
  Cancel,
  CheckBox,
  CheckBoxOutlineBlank,
  CheckCircle,
  Discount,
  Email,
  Notes,
  Person,
  Phone,
} from "@mui/icons-material";

//Helpers
import { fetchWithTokenJSONPost } from "../../Helpers/network";

//Local components
import QuoteSummaryRow from "./QuoteSummaryRow";
import Selector from "../basics/Selector";
import ModalWindow from "../Modal";
import SetClientDrawer from "../drawers/SetClientDrawer";
import ElementCard from "../../ProComponents/ElementCard";

export default function QuoteSummary({
  data = [],
  setData,
  existingQuote,
  currency,
}) {
  //Type of payment methods
  const paymentMethods = [
    "Contado",
    "Crédito",
    "50% por adelantado, 50% contra entrega",
  ];

  const clientTypes = ["Física", "Jurídica", "Otra", "Todos"];

  // ==========> Component States <==========
  //State for open discount setting
  const [addDiscount, setAddDiscount] = useState(false);
  //State for taxes percentage
  const [taxesPercentage, setTaxesPercentage] = useState(0.13);
  //State for discount value
  const [discount, setDiscount] = useState(
    existingQuote.billing.discountPercentage
  );
  //State for billing data
  const [client, setClient] = useState(existingQuote.client);
  //State for notes
  const [notes, setNotes] = useState(existingQuote.notes);
  //State for billing additional data
  const [aditional, setAditional] = useState(existingQuote.billing.aditional);
  //State for payment method
  const [paymentMethod, setPaymentMethod] = useState(
    existingQuote.billing.paymentMethod
  );
  //State for fetch loading
  const [isLoading, setIsLoading] = useState(false);
  //State for open modal
  const [modalOpen, setModalOpen] = useState(false);
  //State for modal text
  const [modalText, setModalText] = useState("");
  //State for modal description
  const [modalDescription, setModalDescription] = useState("");
  //State for include IVA
  const [includeIva, setIncludeIva] = useState(
    existingQuote.billing.taxesPercentage === 0 ? false : true
  );
  //State for clients list
  const [clientsList, setClientsList] = useState([]);
  //State for the selected client
  const [clientForOrder, setClientForOrder] = useState(null);
  //State for open add-client drawer
  const [openClientDrawer, setOpenClientDrawer] = useState(false);
  //State for selected client type by id
  const [selectedClientType, setSelectedClientType] = useState(0);

  useEffect(() => {
    getClientsList();
  }, []);

  // ==========> Component Functions <==========
  //Function to filter clients according to selectedType
  const filteredClients = () => {
    switch (selectedClientType) {
      case 0:
      case 1:
      case 2:
        return clientsList.filter(
          (client) => client.type === selectedClientType
        );
      default:
        return clientsList;
    }
  };

  //Function to get clients list
  const getClientsList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(`/clients/get`, {
        howMany: 3500,
      });
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setClientsList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setClientsList([]);
      //setDataError(true);
    }
  };

  //Amount before taxes and discounts
  const getPretaxAmount = () => {
    let totalAmount = 0;
    data.forEach(
      (product) => (totalAmount += product.price * product.quantity)
    );
    return Number(totalAmount);
  };

  //Amount of discount
  const discountAmount = Number(getPretaxAmount() * (discount / 100));

  //Total amount before taxes
  const totalAmountPretax = getPretaxAmount() - discountAmount;

  //Total amount of taxes
  const taxesAmount = includeIva
    ? Number((getPretaxAmount() - discountAmount) * taxesPercentage)
    : 0.0;

  //Total amount incluiding taxes
  const totalAmount = Number(totalAmountPretax) + Number(taxesAmount);

  //Function to generate products to quote
  const generateProducts = (array) => {
    if (!array.length) {
      return (
        <TableRow>
          <TableCell colSpan={1}>
            <Typography>No tienes productos seleccionados</Typography>
          </TableCell>
        </TableRow>
      );
    } else {
      return array.map((product) => (
        <QuoteSummaryRow
          key={`${product.productName}-${product.quantity}-${product.price}`}
          product={product}
          data={data}
          currency={currency}
          setData={setData}
        />
      ));
    }
  };

  //Function to get the total amount of the quote
  const generateProductsTotalAmount = (array) => {
    if (array.length > 0) {
      return (
        <TableRow>
          <TableCell colSpan={1}>
            <Stack justifyContent="center" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                  onClick={() => {
                    setIncludeIva(!includeIva);
                  }}
                >
                  {includeIva ? (
                    <CheckBox color="secondary" />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </IconButton>
                <Typography>Incluir IVA</Typography>
                <TextField
                  sx={{ maxWidth: "100px" }}
                  value={Number(taxesPercentage * 100).toFixed(2)}
                  onChange={(e) => {
                    setTaxesPercentage(Number(e.target.value / 100).toFixed(2));
                  }}
                  inputProps={{
                    min: 0,
                    max: 100,
                  }}
                  type="number"
                />
                <Typography>%</Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", marginBottom: "10px" }}
                spacing={1}
              >
                <Stack alignItems="flex-start">
                  <Typography>Subtotal</Typography>

                  {discount > 0 && (
                    <Typography>{`Descuento ${discount}%`}</Typography>
                  )}
                  <Typography>IVA</Typography>
                  <Typography>Total</Typography>
                </Stack>
                <Stack alignItems="flex-end">
                  <Typography>{`${
                    currency === "USD" ? "$" : "₡"
                  }${getPretaxAmount().toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}</Typography>

                  {discount > 0 && (
                    <Typography>{`(${
                      currency === "USD" ? "$" : "₡"
                    }${discountAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })})`}</Typography>
                  )}
                  <Typography>{`${
                    currency === "USD" ? "$" : "₡"
                  }${taxesAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}</Typography>
                  <Typography>{`${
                    currency === "USD" ? "$" : "₡"
                  }${totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}</Typography>
                </Stack>
              </Stack>
              {addDiscount && (
                <Stack direction="row" sx={{ marginBottom: "10px" }}>
                  <IconButton
                    onClick={() => {
                      setDiscount(0);
                      setAddDiscount(false);
                    }}
                  >
                    <Cancel color="error" />
                  </IconButton>
                  <TextField
                    label="Desc(%)"
                    focused
                    color="secondary"
                    type="number"
                    value={discount}
                    onChange={(e) => {
                      setDiscount(e.target.value);
                    }}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                  />
                  <IconButton
                    onClick={() => {
                      setAddDiscount(false);
                    }}
                  >
                    <CheckCircle color="success" />
                  </IconButton>
                </Stack>
              )}
              {!addDiscount && (
                <Button
                  variant="outlined"
                  sx={{ color: "secondary" }}
                  endIcon={<Discount />}
                  onClick={() => {
                    setAddDiscount(true);
                  }}
                >
                  Aplicar un Descuento
                </Button>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      );
    }
  };

  //Function to generate quote
  const generateQuote = async () => {
    setIsLoading(true);

    const quote = {
      client: {
        name: client.name,
        email: client.email,
        phone: client.phone,
        id: client.id,
      },
      billing: {
        taxesPercentage: includeIva ? (taxesPercentage * 100).toFixed(2) : 0,
        discountPercentage: discount,
        paymentMethod: paymentMethods[paymentMethod],
        aditional: aditional,
        currency: currency,
      },
      notes: notes,
      products: data,
      clientId: filteredClients()[clientForOrder].id,
    };

    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/quotes/create",
        quote
      );
      const jsonResponse = await requestResponse.json();

      if (jsonResponse.status === "ok") {
        localStorage.removeItem("quote");
        window.location.href = `/quote?id=${jsonResponse.data.quoteNumber}`;
      } else {
        setModalText("No se pudo realizar la acción.");
        setModalDescription(jsonResponse.description);
        setModalOpen(true);
      }
    } catch (error) {
      setModalText("No se pudo realizar la acción.");
      setModalDescription(
        "Error al hacer los cambios, por favor verifica tu conexión a internet e inténtalo nuevamente."
      );
      setModalOpen(true);
    }
  };

  //Function to validate quote before generation
  const validateQuote = () => {
    if (client.name === "" || data.length < 1) {
      return (
        <Alert severity="warning">
          Por favor, asegúrese de seleccionar correctamente el cliente y agregar
          al menos un producto a la cotización.
        </Alert>
      );
    } else {
      return isLoading ? (
        <CircularProgress />
      ) : (
        <Button onClick={generateQuote}>GENERAR COTIZACIÓN</Button>
      );
    }
  };

  // ==========> Component <==========
  return (
    <Card raised>
      <SetClientDrawer
        open={openClientDrawer}
        setOpen={setOpenClientDrawer}
        getData={getClientsList}
      />
      <Grid container justifyContent="center" spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <ElementCard
            ops={{
              title: "Resumen de productos seleccionados",
              details: [
                "Resumen de los productos seleccionados e ingreso de datos importantes para generar la cotización.",
              ],
            }}
          />
        </Grid>

        <Grid item xs={12} align="left">
          <Typography fontWeight="bold" color="secondary">
            Productos seleccionados
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TableContainer>
            <Table aria-label="products-to-quote-table">
              <TableBody>
                {generateProducts(data)}
                {generateProductsTotalAmount(data)}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} align="left">
          <Typography fontWeight="bold" color="secondary">
            Información del cliente
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Tipo de Identificación</Typography>
            <Selector
              fullWidth
              content={clientTypes}
              value={selectedClientType}
              handleChange={(e) => {
                setSelectedClientType(e.target.value);
              }}
              width="100%"
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Clientes registrados</Typography>
            <Selector
              fullWidth
              content={filteredClients().map((client) => client.name)}
              value={clientForOrder}
              handleChange={(e) => {
                setClientForOrder(e.target.value);
                setClient({
                  name: filteredClients()[e.target.value].name,
                  id: filteredClients()[e.target.value].idDocument,
                  email: filteredClients()[e.target.value].email,
                  phone: filteredClients()[e.target.value].phoneNumber,
                });
              }}
              width="100%"
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="outlined"
            sx={{ color: "secondary" }}
            endIcon={<Add />}
            onClick={() => {
              setOpenClientDrawer(true);
            }}
          >
            Registrar Nuevo cliente
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Identificación</Typography>
            <TextField
              fullWidth
              placeholder="Ej: 1-1814-0155"
              onChange={(e) => setClient({ ...client, id: e.target.value })}
              value={client.id}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentInd />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Teléfono</Typography>
            <TextField
              fullWidth
              placeholder="Ej: 7018-7618"
              onChange={(e) => setClient({ ...client, phone: e.target.value })}
              value={client.phone}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Correo electrónico</Typography>
            <TextField
              fullWidth
              placeholder="Ej: micorreo@elcocrc.com"
              value={client.email}
              onChange={(e) => setClient({ ...client, email: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography fontWeight="bold" color="secondary">
            Información de la cotización
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Forma de pago</Typography>
            <Selector
              width="100%"
              content={paymentMethods}
              value={paymentMethod}
              handleChange={(e) => {
                setPaymentMethod(e.target.value);
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Información de entrega</Typography>
            <TextField
              fullWidth
              placeholder="Ej: A convenir según entrega del vehículo"
              value={aditional}
              onChange={(e) => {
                setAditional(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Notas de cotización</Typography>
            <TextField
              fullWidth
              multiline
              placeholder="Agregue notas a la cotización"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Notes />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          {validateQuote()}
        </Grid>
      </Grid>
      <ModalWindow
        isOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={modalText}
        text={modalDescription}
        leftText="OK"
        leftClick={() => {
          setModalOpen(false);
        }}
      />
    </Card>
  );
}
